import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 403 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                    refreshToken,
                });

                const { accessToken, refreshToken: newRefreshToken } = response.data;

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export const authAPI = {
    login: (username, password) =>
        api.post('/auth/login', { username, password }),
    
    logout: () =>
        api.post('/auth/logout'),
    
    refresh: (refreshToken) =>
        api.post('/auth/refresh', { refreshToken }),
};

export const adminAPI = {
    getTeachers: () => api.get('/admin/teachers'),
    createTeacher: (data) => api.post('/admin/teachers', data),
    updateTeacher: (id, data) => api.put(`/admin/teachers/${id}`, data),
    deleteTeacher: (id) => api.delete(`/admin/teachers/${id}`),

    getStudents: () => api.get('/admin/students'),
    getStudent: (id) => api.get(`/admin/students/${id}`),
    createStudent: (data) => api.post('/admin/students', data),
    updateStudent: (id, data) => api.put(`/admin/students/${id}`, data),
    deleteStudent: (id) => api.delete(`/admin/students/${id}`),
    resetPassword: (id, newPassword) =>
        api.post(`/admin/students/${id}/reset-password`, { newPassword }),

    getAreas: (params) => api.get('/admin/areas', { params: params || {} }),
    getArea: (id) => api.get(`/admin/areas/${id}`),
    createArea: (data) => api.post('/admin/areas', data),
    updateArea: (id, data) => api.put(`/admin/areas/${id}`, data),
    deleteArea: (id) => api.delete(`/admin/areas/${id}`),

    getLevels: (areaId) => api.get('/admin/levels', { params: { areaId } }),
    getLevel: (id) => api.get(`/admin/levels/${id}`),
    createLevel: (data) => api.post('/admin/levels', data),
    updateLevel: (id, data) => api.put(`/admin/levels/${id}`, data),
    deleteLevel: (id) => api.delete(`/admin/levels/${id}`),

    getLessons: (levelId) => api.get('/admin/lessons', { params: { levelId } }),
    getLesson: (id) => api.get(`/admin/lessons/${id}`),
    createLesson: (data) => api.post('/admin/lessons', data),
    updateLesson: (id, data) => api.put(`/admin/complex-lesson/lessons/${id}`, data),
    deleteLesson: (id) => api.delete(`/admin/lessons/${id}`),

    getTexts: (lessonId) => api.get('/admin/texts', { params: { lessonId } }),
    getText: (id) => api.get(`/admin/texts/${id}`),
    createText: (data) => api.post('/admin/texts', data),
    updateText: (id, data) => api.put(`/admin/texts/${id}`, data),
    deleteText: (id) => api.delete(`/admin/texts/${id}`),
    updateSentence: (textId, sentenceId, data) =>
        api.put(`/admin/texts/${textId}/sentences/${sentenceId}`, data),
    uploadTextImage: (formData) => api.post('/admin/upload-text-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
    uploadSentenceAudio: (formData) => api.post('/admin/upload-sentence-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
    uploadTextAudio: (formData) => api.post('/admin/upload-text-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
    uploadVideo: (formData) => api.post('/admin/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    sliceTextAudioForSentence: (textId, sentenceId, data) =>
        api.post(`/admin/texts/${textId}/sentences/${sentenceId}/slice-audio`, data),
    sliceAudio: (data) => api.post('/admin/slice-audio', data),
    
    generateTTS: (data) => api.post('/tts/generate', data),

    getFormulas: (levelId, lessonId) => api.get('/admin/formulas', { params: { levelId, lessonId } }),
    getFormula: (id) => api.get(`/admin/formulas/${id}`),
    createFormula: (data) => api.post('/admin/formulas', data),
    updateFormula: (id, data) => api.put(`/admin/formulas/${id}`, data),
    deleteFormula: (id) => api.delete(`/admin/formulas/${id}`),
    getStudentProgress: (studentId) => api.get(`/admin/formulas/progress/${studentId}`),
    updateStudentProgress: (studentId, formulaId, data) =>
        api.post(`/admin/formulas/progress/${studentId}/${formulaId}`, data),

    getExercises: (textId) => api.get('/admin/exercises', { params: { textId } }),
    getExercise: (id) => api.get(`/admin/exercises/${id}`),
    createExercise: (data) => api.post('/admin/exercises', data),
    updateExercise: (id, data) => api.put(`/admin/exercises/${id}`, data),
    deleteExercise: (id) => api.delete(`/admin/exercises/${id}`),
    updateExerciseQuestion: (exerciseId, questionId, data) =>
        api.put(`/admin/exercises/${exerciseId}/questions/${questionId}`, data),
    checkExercise: (data) => api.post('/admin/exercises/check', data),

    getVideos: (lessonId) => api.get('/admin/videos', { params: { lessonId } }),
    getVideo: (id) => api.get(`/admin/videos/${id}`),
    createVideo: (data) => api.post('/admin/videos', data),
    updateVideo: (id, data) => api.put(`/admin/videos/${id}`, data),
    deleteVideo: (id) => api.delete(`/admin/videos/${id}`),

    getHomeworks: (studentId) => api.get('/admin/homework', { params: { studentId } }),
    getHomework: (id) => api.get(`/admin/homework/${id}`),
    createHomework: (data) => api.post('/admin/homework', data),
    updateHomework: (id, data) => api.put(`/admin/homework/${id}`, data),
    deleteHomework: (id) => api.delete(`/admin/homework/${id}`),
    getHomeworkSubmissions: (id) => api.get(`/admin/homework/${id}/submissions`),
    reviewSubmission: (submissionId, data) => 
        api.put(`/admin/homework/submissions/${submissionId}/review`, data),

    createComplexLesson: (data) => api.post('/admin/complex-lesson/create-complex', data),
    getLessons: (params) => api.get('/admin/complex-lesson/lessons', { params }),
    getLesson: (id) => api.get(`/admin/complex-lesson/lessons/${id}`),
    deleteLesson: (id) => api.delete(`/admin/complex-lesson/lessons/${id}`),
    
    // Быстрое обновление порядка урока
    updateLessonSortOrder: (id, sortOrder) => api.patch(`/admin/lessons-blocks/lessons/${id}/sort-order`, { sort_order: sortOrder }),
    
    // Блочные уроки (новая система)
    getLessonsBlocks: (params) => api.get('/admin/lessons-blocks/lessons', { params }),
    createLessonBlocks: (data) => api.post('/admin/lessons-blocks/create-lesson', data),
    updateLessonBlocks: (id, data) => api.put(`/admin/lessons-blocks/lessons/${id}`, data),
    getLessonBlocks: (id, params = {}) => api.get(`/admin/lessons-blocks/lessons/${id}`, { params }),
    deleteLessonBlocks: (id) => api.delete(`/admin/lessons-blocks/lessons/${id}`),
    
    // Аудиозаписи
    getAudioRecordings: (params) => api.get('/admin/audio-recordings', { params }),
    getAudioRecording: (id) => api.get(`/admin/audio-recordings/${id}`),
    deleteAudioRecording: (id) => api.delete(`/admin/audio-recordings/${id}`),
    archiveAudioRecordings: (ids) => api.post('/admin/audio-recordings/archive', { ids }),
    unarchiveAudioRecordings: (ids) => api.post('/admin/audio-recordings/unarchive', { ids }),
    
    // Независимые тесты (старая система)
    getStandaloneTests: (params) => api.get('/admin/standalone-tests', { params }),
    getStandaloneTest: (id) => api.get(`/admin/standalone-tests/${id}`),
    createStandaloneTest: (data) => api.post('/admin/standalone-tests', data),
    updateStandaloneTest: (id, data) => api.put(`/admin/standalone-tests/${id}`, data),
    deleteStandaloneTest: (id) => api.delete(`/admin/standalone-tests/${id}`),
    assignTest: (data) => api.post('/admin/standalone-tests/assign', data),
    getTestResults: (id) => api.get(`/admin/standalone-tests/${id}/results`),
    
    // Тесты с блоками (новая система)
    getTests: (params) => api.get('/admin/tests-blocks/tests', { params }),
    getTestBlocks: (id) => api.get(`/admin/tests-blocks/tests/${id}`),
    createTestBlocks: (data) => api.post('/admin/tests-blocks/tests', data),
    updateTestBlocks: (id, data) => api.put(`/admin/tests-blocks/tests/${id}`, data),
    deleteTestBlocks: (id) => api.delete(`/admin/tests-blocks/tests/${id}`),
    updateTestSortOrder: (id, sortOrder) => api.patch(`/admin/tests-blocks/tests/${id}/sort-order`, { sort_order: sortOrder }),
    
    // Проверка тестов с блоками
    getTestBlocksResults: (filter) => api.get('/admin/checking/test-blocks/results', { params: { filter } }),
    getTestBlocksResultDetail: (resultId) => api.get(`/admin/checking/test-blocks/results/${resultId}`),
    updateTestBlocksWritingScores: (resultId, data) => api.put(`/admin/checking/test-blocks/results/${resultId}/writing`, data),
    reopenTest: (resultId) => api.post(`/admin/checking/test-blocks/results/${resultId}/reopen`),
    
    // Проверка домашних заданий
    getExerciseSubmissions: (params) => api.get('/admin/checking/exercises', { params }),
    getAudioSubmissions: (params) => api.get('/admin/checking/audio', { params }),
    getHomeworkCheckingSubmissions: (params) => api.get('/admin/checking/homework', { params }),
    getHomeworkCheckingSubmission: (id) => api.get(`/admin/checking/homework/${id}`),
    reviewHomeworkCheckingSubmission: (id, data) => api.put(`/admin/checking/homework/${id}/review`, data),
    getExerciseSubmission: (id) => api.get(`/admin/checking/exercises/${id}`),
    checkExerciseSubmission: (id, data) => api.put(`/admin/checking/exercises/${id}/check`, data),
    
    // Проверка аудиозаписей
    addAudioFeedback: (id, data) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
            return api.post(`/admin/checking/audio/${id}/feedback`, data, {
                transformRequest: [
                    (body, headers) => {
                        delete headers['Content-Type'];
                        return body;
                    },
                ],
            });
        }
        return api.post(`/admin/checking/audio/${id}/feedback`, data);
    },
    getAudioFeedback: (id) => api.get(`/admin/checking/audio/${id}/feedback`),
    
    // Методические материалы учителя
    getTeacherMaterials: (params) => api.get('/admin/teacher-materials/materials', { params }),
    getTeacherMaterial: (id) => api.get(`/admin/teacher-materials/materials/${id}`),
    createTeacherMaterial: (data) => api.post('/admin/teacher-materials/materials', data),
    updateTeacherMaterial: (id, data) => api.put(`/admin/teacher-materials/materials/${id}`, data),
    deleteTeacherMaterial: (id) => api.delete(`/admin/teacher-materials/materials/${id}`),
    togglePinMaterial: (id) => api.put(`/admin/teacher-materials/materials/${id}/toggle-pin`),
    
    // Результаты тестов
    getLessonTestResults: (params) => api.get('/admin/checking/lesson-tests', { params }),
    getStandaloneTestResults: (params) => api.get('/admin/checking/standalone-tests/results', { params }),
    
    // Статистика студента
    getStudentStats: (studentId) => api.get(`/admin/checking/student/${studentId}/stats`),
};

export const audioAPI = {
    uploadAudio: (formData) => api.post('/audio/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getMyRecordings: (params) => api.get('/audio/my-recordings', { params }),
    getStudentRecordings: (studentId) => api.get(`/audio/student/${studentId}`),
    deleteRecording: (id) => api.delete(`/audio/${id}`),
};

export const studentAPI = {
    getProfile: () => api.get('/student/profile'),
    
    getAreas: (params) => api.get('/student/areas', { params }),
    
    getLevels: (areaId) => api.get('/student/levels', { params: { areaId } }),
    
    getLessons: (levelId) => api.get('/student/lessons', { params: { levelId } }),
    getLesson: (id, options = {}) => api.get(`/student/lessons/${id}`, { params: options }),
    
    getText: (id) => api.get(`/student/texts/${id}`),
    
    getFormulas: () => api.get('/student/formulas'),

    getExercise: (id) => api.get(`/student/exercises/${id}`),
    /** Аудио для фонетического диктанта (файл или TTS), responseType: blob */
    getPhoneticDictationAudio: (questionId) =>
        api.get(`/student/exercise-questions/${questionId}/phonetic-audio`, { responseType: 'blob' }),
    checkExercise: (data) => api.post('/student/exercises/check', data),
    submitExercise: (id, data) => api.post(`/student/exercises/${id}/submit`, data),
    saveExerciseAttempt: (id, data) => api.post(`/student/exercises/${id}/save-attempt`, data),
    getLessonExerciseAttempts: (lessonId) => api.get(`/student/lessons/${lessonId}/exercise-attempts`),
    getMySubmissions: (params) => api.get('/student/exercises/my-submissions', { params }),
    
    // Аудиозаписи
    uploadAudio: (formData) =>
        api.post('/student/audio/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 20 * 60 * 1000,
        }),
    getAudioRecordings: (params) => api.get('/student/audio/recordings', { params }),
    getAudioFeedback: (id) => api.get(`/student/audio/${id}/feedback`),
    
    // Независимые тесты
    getAssignedTests: () => api.get('/student/tests/assigned'),
    getAvailableTests: (params) => api.get('/student/tests/available', { params }),
    startTest: (id) => api.get(`/student/tests/${id}/start`),
    submitTest: (id, data) => api.post(`/student/tests/${id}/submit`, data),
    getMyTestResults: () => api.get('/student/tests/my-results'),
    getTestResultDetails: (resultId) => api.get(`/student/tests/results/${resultId}/details`),
    
    // Домашние задания
    getMyHomework: () => api.get('/homework/student/my'),
    getUnreadHomeworkCount: () => api.get('/homework/student/unread-count'),
    markHomeworkAsRead: (id) => api.post(`/homework/${id}/mark-read`),
    getHomeworkSubmission: (homeworkId) => api.get(`/homework/${homeworkId}/submission`),
    saveHomeworkSubmission: (homeworkId, data) =>
        api.post(`/homework/${homeworkId}/submission`, data),
    submitHomework: (homeworkId) => api.put(`/homework/${homeworkId}/submission/submit`),
    uploadHomeworkAttachment: (homeworkId, formData) =>
        api.post(`/homework/${homeworkId}/submission/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
};

export const studentGroupsAPI = {
    getGroups: () => api.get('/admin/student-groups'),
    getGroup: (id) => api.get(`/admin/student-groups/${id}`),
    createGroup: (data) => api.post('/admin/student-groups', data),
    updateGroup: (id, data) => api.put(`/admin/student-groups/${id}`, data),
    deleteGroup: (id) => api.delete(`/admin/student-groups/${id}`),
};

export const homeworkAPI = {
    // Для админа
    getAllHomework: (params) => api.get('/homework/all', { params }),
    getHomework: (id) => api.get(`/homework/${id}`),
    createHomework: (data) => api.post('/homework/create', data),
    updateHomework: (id, data) => api.put(`/homework/${id}`, data),
    deleteHomework: (id) => api.delete(`/homework/${id}`),
    getStudentsList: () => api.get('/homework/students/list'),
};

export const materialsAPI = {
    // Для админа
    getMaterials: () => api.get('/admin/materials/all'),
    getMaterial: (id) => api.get(`/admin/materials/${id}`),
    createMaterial: (formData) => {
        return api.post('/admin/materials/create', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    updateMaterial: (id, formData) => {
        return api.put(`/admin/materials/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    deleteMaterial: (id) => api.delete(`/admin/materials/${id}`),
    
    // Для студента
    getStudentMaterials: () => api.get('/student/materials/all'),
    getStudentMaterial: (id) => api.get(`/student/materials/${id}`),
};

export const studentModulesAPI = {
    getStudentModules: (studentId) => api.get(`/admin/students/${studentId}/modules`),
    assignModule: (studentId, data) => api.post(`/admin/students/${studentId}/modules`, data),
    removeModule: (studentId, moduleId) => api.delete(`/admin/students/${studentId}/modules/${moduleId}`),
    getAvailableModules: () => api.get('/admin/students/available-modules'),
    getModuleLessons: (studentId, moduleId) =>
        api.get(`/admin/students/${studentId}/modules/${moduleId}/lessons`),
    setModuleLessons: (studentId, moduleId, payload) =>
        api.put(
            `/admin/students/${studentId}/modules/${moduleId}/lessons`,
            Array.isArray(payload) ? { lesson_ids: payload } : payload
        ),
};

export const studentFormulasAPI = {
    getStudentFormulas: (studentId) => api.get(`/admin/students/${studentId}/formulas`),
    updateFormulaStatus: (studentId, formulaId, data) => api.post(`/admin/students/${studentId}/formulas/${formulaId}`, data),
    getFormulaStats: (studentId) => api.get(`/admin/students/${studentId}/formulas/stats`),
};

export default api;
